/**
 * SOFT_DELETE_STANDARD (F4-A) — papelera local-first por tipo de entidad.
 *
 * RESTORE-ONLY a propósito (variante field-check): el write-path convierte el
 * DELETE del cliente en soft-delete y el borrado físico lo hace SOLO el job de
 * purga del servidor (TRASH_PURGE_DAYS). Cada fila muestra "borrado hace X" y
 * la cuenta atrás de purga. Restaurar registra `restored` en el feed y ofrece
 * deshacer (vuelve a la papelera).
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { ArchiveRestore, Trash2 } from "lucide-solid";
import {
  daysUntilPurge,
  getTrashedQuery,
  restoreCourseModuleCascadeMutation,
  restoreTrashedMutation,
  TRASH_KINDS,
  trashAgainMutation,
  trashCourseModuleCascadeMutation,
  trashedLabel,
  type TrashKind,
  trashQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useUndo } from "@/lib/undo";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

const KIND_TITLES: Record<TrashKind, MessageDescriptor> = {
  cohort: msg`Cohorts`,
  course_module: msg`Modules`,
  announcement: msg`Announcements`,
  forum_thread: msg`Forum threads`,
  schedule_slot: msg`Schedule slots`,
  program: msg`Programs`,
  resource: msg`Resources`,
};

interface TrashedRow {
  id: string;
  deleted_at: string | null;
  [key: string]: unknown;
}

const KindSection: Component<{ kind: TrashKind }> = (props) => {
  const { t, locale } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const { userId } = useSession();
  const { registerUndo } = useUndo();

  const rows = createPowerSyncQuery<TrashedRow>({
    queryKey: () => trashQueryKeys.byKind(userId(), props.kind),
    db,
    queryFn: () => {
      const d = drizzle();
      return d ? getTrashedQuery(d, props.kind) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const restore = (row: TrashedRow, kind: TrashKind) => {
    const d = drizzle();
    if (!d) {
      return;
    }
    void (async () => {
      // course_module restaura EN PAREJA con su lesson/resource/assignment.
      if (kind === "course_module") {
        await restoreCourseModuleCascadeMutation(d, row.id);
      } else {
        await restoreTrashedMutation(d, kind, row.id);
      }
      registerUndo(t(msg`Item restored`), async () => {
        const dd = drizzle();
        if (!dd) {
          return;
        }
        if (kind === "course_module") {
          await trashCourseModuleCascadeMutation(dd, row.id);
        } else {
          await trashAgainMutation(dd, kind, row.id);
        }
      });
    })();
  };

  const fmtDeleted = (ts: string | null) => {
    if (!ts) {
      return "";
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime())
      ? ""
      : new Intl.DateTimeFormat(locale(), { dateStyle: "medium", timeStyle: "short" }).format(d);
  };

  return (
    <Show when={(rows.data ?? []).length > 0}>
      <section class="space-y-2" data-testid={`trash-section-${props.kind}`}>
        <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {t(KIND_TITLES[props.kind])}
        </h2>
        <ul class="space-y-2">
          <For each={rows.data ?? []}>
            {(row) => (
              <li
                class="flex items-center gap-3 rounded-2xl border border-border bg-surface p-3"
                data-testid="trash-item"
              >
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium">
                    {trashedLabel(props.kind, row) ?? t(msg`Untitled`)}
                  </p>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Deleted`)} {fmtDeleted(row.deleted_at)}
                    <Show when={daysUntilPurge(row.deleted_at)}>
                      {(daysLeft) => {
                        const days = daysLeft();
                        return (
                          <span>
                            {" · "}
                            {t(msg`Permanently deleted in ${days} days`)}
                          </span>
                        );
                      }}
                    </Show>
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="trash-restore"
                  onClick={() => restore(row, props.kind)}
                  class="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  <ArchiveRestore class="h-4 w-4" />
                  {t(msg`Restore`)}
                </button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
};

export const TrashView: Component = () => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const { userId } = useSession();

  // Recuento total (para el empty state global): una query por kind ya montada
  // en las secciones; aquí solo un memo ligero sobre las mismas queries.
  const counts = TRASH_KINDS.map((kind) =>
    createPowerSyncQuery<TrashedRow>({
      queryKey: () => trashQueryKeys.byKindCount(userId(), kind),
      db,
      queryFn: () => {
        const d = drizzle();
        return d ? getTrashedQuery(d, kind) : null;
      },
      enabled: () => Boolean(drizzle()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
      suspense: false,
    }),
  );
  const total = createMemo(() => counts.reduce((acc, q) => acc + (q.data?.length ?? 0), 0));

  return (
    <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
      <PageHeader title={t(msg`Trash`)} icon={Trash2} />
      <Show
        when={total() > 0}
        fallback={
          <div data-testid="trash-empty">
            <EmptyState
              icon={Trash2}
              title={t(msg`Trash is empty`)}
              description={t(msg`Deleted items land here and can be restored.`)}
            />
          </div>
        }
      >
        <div class="space-y-6" data-testid="trash-list">
          <For each={[...TRASH_KINDS]}>{(kind) => <KindSection kind={kind} />}</For>
        </div>
      </Show>
    </div>
  );
};
